import moment from "moment";

type AdditionalFieldDef = {
  type: string;
  name: string;
};

export type OtherFieldExcelColumn = {
  header: string;
  key: string;
  width: number;
};

export function parseOtherFieldsValues(raw?: string | null): Record<string, unknown> {
  if (!raw) return {};

  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return {};

    return parsed.reduce<Record<string, unknown>>((acc, item) => ({ ...acc, ...item }), {});
  } catch {
    return {};
  }
}

export function formatOtherFieldValueForExcel(key: string, value: unknown): string {
  if (value == null || value === "") return "";

  const type = key.split("_custom_")[0];

  switch (type) {
    case "employee":
      return Array.isArray(value)
        ? value.map((person: { label?: string; value?: string }) => person.label || person.value).filter(Boolean).join(", ")
        : "";

    case "date": {
      const dateValue = value as { startDate?: string; endDate?: string };
      return dateValue.startDate ? moment(dateValue.startDate).format("DD.MM.YYYY") : "";
    }

    case "location":
    case "department":
    case "sector":
    case "single":
      return (value as { name?: string })?.name ?? "";

    case "many":
      return Array.isArray(value)
        ? value.map((item) => (typeof item === "string" ? item : (item as { name?: string })?.name)).filter(Boolean).join(", ")
        : "";

    case "free":
    case "grade":
      return String(value);

    default:
      return typeof value === "object" ? JSON.stringify(value) : String(value);
  }
}

export function buildOtherFieldExcelColumns(additionalFieldsJson?: string | null): OtherFieldExcelColumn[] {
  if (!additionalFieldsJson) return [];

  try {
    const fields: AdditionalFieldDef[] = JSON.parse(additionalFieldsJson);
    if (!Array.isArray(fields)) return [];

    return fields.map((field, idx) => ({
      header: field.name,
      key: `${field.type}_custom_${idx}`,
      width: 30,
    }));
  } catch {
    return [];
  }
}

export function mapOtherFieldValuesToExcelRow(
  otherFieldsValues: string | undefined,
  columns: OtherFieldExcelColumn[],
): Record<string, string> {
  const values = parseOtherFieldsValues(otherFieldsValues);

  return Object.fromEntries(columns.map((column) => [column.key, formatOtherFieldValueForExcel(column.key, values[column.key])]));
}
